/**
 * NodeInfoBar — tabbed bottom sheet for the selected build node.
 *
 * Tabs: Overview (default) / Data (INPUT_DATA only) / Last run / Validation.
 *
 * The Overview tab mirrors PlanNodeDetailPanel's vocabulary as a sectioned
 * grid so build nodes read the same way plan nodes do:
 *   1. WHAT THIS NODE DOES — description / logic narrative + logic steps
 *      (+ the VALIDATE-node claim block)
 *   2. REQUIREMENTS — typed requirement_refs chips ("this node implements
 *      REQ-NNN from <spec workspace>"); legacy handoff_item_ids render only
 *      when no typed ref exists, tagged 'legacy'
 *   3. INPUTS — upstream dependencies, plan-panel row style
 *   4. OUTPUT — declared artifacts, same treatment
 *
 * Two collapse states:
 *   'minimal'  (40px)  — just the header row
 *   'expanded' (320px) — header + active tab content
 */

import React, { useMemo, useState, useEffect } from 'react';
import {
    ChevronUp, ChevronDown, ExternalLink,
    AlertCircle, CheckCircle2, XCircle, FileCog, Loader2, Link2, ArrowRight,
} from 'lucide-react';
import type { NodeDefinition, ModelRun, RunStatus } from '../../types/modeling';
import {
    getNodeTypeColor, getNodeTypeLabel, getEngineLabel,
} from '../../types/modeling';
import type { InfoBarCollapse } from '../../stores/processViewerStore';
import { useProcessViewerStore } from '../../stores/processViewerStore';
import { decodeEntities } from '../../utils/planGraphData';
import { formatNumber } from '../../utils/plural';
import DataUploadPanel from './DataUploadPanel';

type TabId = 'overview' | 'data' | 'validation';

interface NodeInfoBarProps {
    node: NodeDefinition;
    projectId: string;
    modelId: string;
    authToken: string;
    /** Logged-in user id — forwarded to DataUploadPanel for the report-library
     *  upload step (BAR-308). */
    userId?: string;
    lastRun: ModelRun | null;
    activeRunProgress: ModelRun | null;
    collapse: InfoBarCollapse;
    onCollapseChange: (state: InfoBarCollapse) => void;
    onOpenTool: () => void;
    /** Click-through from a requirement chip to the Spec panel. Key is the
     *  composite `${source_workspace_id}::${requirement_id}` (SpecPanel's
     *  reqKey()). Absent → chips render as inert spans. */
    onRequirementClick?: (requirementKey: string) => void;
    /** Select another node (used by Input rows to jump to the source node).
     *  Absent → Input rows render as inert. */
    onSelectNode?: (nodeId: string) => void;
    /** Open a produced output artifact (file/folder). Absent → Output rows inert. */
    onOpenArtifact?: (nodeId: string, artifactName: string) => void;
}

export default function NodeInfoBar({
    node, projectId, modelId, authToken, userId, lastRun, activeRunProgress, collapse, onCollapseChange, onOpenTool,
    onRequirementClick, onSelectNode, onOpenArtifact,
}: NodeInfoBarProps) {
    const color = getNodeTypeColor(node.node_type);

    const run = activeRunProgress || lastRun;
    const nodeResult = run?.node_results?.find((nr) => nr.node_id === node.node_id);
    const artifactPaths = nodeResult?.artifact_paths ?? {};
    const validationResults = useMemo(
        () => run?.validation_results?.filter((vr) => vr.source_ids.includes(node.node_id)) ?? [],
        [run, node.node_id]
    );

    const validationPass = validationResults.filter((v) => v.passed).length;
    const validationFail = validationResults.length - validationPass;

    const isInputData = node.node_type === 'input_data';

    // Tab state — preferred tab persists per session across node selections.
    const [activeTab, setActiveTab] = useState<TabId>('overview');

    // On node switch: INPUT_DATA nodes open on their Upload Data tab (uploading
    // is the node's primary action — double-click lands here too); a failing
    // validation takes the spotlight on a non-input node sitting on the default
    // tab; and a stale 'data' tab recovers to Overview when the new node isn't
    // INPUT_DATA (the Data tab itself disappears for those nodes).
    useEffect(() => {
        if (isInputData) {
            setActiveTab('data');
        } else if (validationFail > 0 && activeTab === 'overview') {
            setActiveTab('validation');
        } else if (activeTab === 'data') {
            setActiveTab('overview');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node.node_id]);

    const toggleCollapse = () =>
        onCollapseChange(collapse === 'expanded' ? 'minimal' : 'expanded');

    const height = collapse === 'expanded' ? 320 : 40;

    const tabs: Array<{ id: TabId; label: string; count?: number; badge?: 'warning' | 'success' | 'danger' }> = [
        { id: 'overview',   label: 'Overview' },
        ...(isInputData ? [{ id: 'data' as TabId, label: 'Upload data' }] : []),
        { id: 'validation', label: 'Validation', count: validationResults.length, badge: validationFail > 0 ? 'danger' : validationPass > 0 ? 'success' : undefined },
    ];

    return (
        <div
            className="absolute bottom-0 left-0 right-0 bg-surface border-t border-border-default shadow-bark-lg transition-all duration-300 z-10 flex flex-col"
            style={{ height }}
        >
            {/* ---- Header (always visible) ---- */}
            <div
                className="flex items-center gap-2 px-4 h-10 flex-shrink-0 cursor-pointer select-none border-b border-divider"
                onClick={toggleCollapse}
            >
                {/* Type chip — dot + uppercase label in node-type colour */}
                <span
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md font-body text-[10px] font-bold uppercase tracking-[0.08em] flex-shrink-0"
                    style={{ backgroundColor: `${color}14`, color }}
                >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                    {getNodeTypeLabel(node.node_type)}
                </span>

                <span className="text-sm font-semibold text-fg flex-1 truncate">{decodeEntities(node.name)}</span>

                {node.engine && (
                    <span className="font-mono text-[10px] text-fg-tertiary bg-bg-warm px-1.5 py-0.5 rounded">
                        {getEngineLabel(node.engine)}
                    </span>
                )}

                {nodeResult && (
                    <StatusPill status={nodeResult.status} timeMs={nodeResult.execution_time_ms} />
                )}

                {isInputData ? (
                    /* INPUT_DATA has no notebook — open the uploaded DATA, not
                       code (owner #1/#2). */
                    onOpenArtifact && node.outputs.length > 0 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onOpenArtifact(node.node_id, node.outputs[0].artifact.artifact_name); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-accent-hover bg-accent-tint rounded-md hover:bg-accent hover:text-white transition-colors"
                        >
                            <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                            Open data
                        </button>
                    )
                ) : node.node_type === 'output' ? (
                    /* OUTPUT node has no engine — opening it shows the produced
                       DATA (resolved from the producer) as a table, not code
                       (owner #3). onOpenTool → Tool view → DataPreview. */
                    <button
                        onClick={(e) => { e.stopPropagation(); onOpenTool(); }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-accent-hover bg-accent-tint rounded-md hover:bg-accent hover:text-white transition-colors"
                    >
                        <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                        Open data
                    </button>
                ) : node.engine ? (
                    <button
                        onClick={(e) => { e.stopPropagation(); onOpenTool(); }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-accent-hover bg-accent-tint rounded-md hover:bg-accent hover:text-white transition-colors"
                    >
                        <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                        Open tool
                    </button>
                ) : null}

                <button
                    onClick={(e) => { e.stopPropagation(); toggleCollapse(); }}
                    className="p-1 text-fg-tertiary hover:text-fg transition-colors"
                    aria-label={collapse === 'expanded' ? 'Collapse' : 'Expand'}
                >
                    {collapse === 'expanded'
                        ? <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
                        : <ChevronUp   className="w-4 h-4" strokeWidth={1.5} />}
                </button>
            </div>

            {/* ---- Tab strip + body (expanded only) ---- */}
            {collapse === 'expanded' && (
                <>
                    <div className="flex items-center gap-1 px-4 pt-2 flex-shrink-0 border-b border-divider">
                        {tabs.map((t) => {
                            const active = t.id === activeTab;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => setActiveTab(t.id)}
                                    className={`relative -mb-px px-3 py-2 flex items-center gap-1.5 text-[13px] font-semibold transition-colors ${
                                        active ? 'text-accent-hover' : 'text-fg-secondary hover:text-fg'
                                    }`}
                                >
                                    {t.label}
                                    {t.count !== undefined && t.count > 0 && (
                                        <span className={`font-mono text-[10px] font-semibold tabular-nums px-1 rounded ${
                                            active ? 'bg-accent-tint text-accent-hover' : 'bg-bg-warm text-fg-tertiary'
                                        }`}>
                                            {t.count}
                                        </span>
                                    )}
                                    {t.badge && (
                                        <span className={`w-1.5 h-1.5 rounded-full ${
                                            t.badge === 'danger'  ? 'bg-danger' :
                                            t.badge === 'success' ? 'bg-success' :
                                                                    'bg-warning'
                                        }`} />
                                    )}
                                    {active && <span className="absolute inset-x-0 -bottom-px h-[2px] bg-accent" />}
                                </button>
                            );
                        })}
                    </div>

                    {/* Overview owns its own section grid (per-section scroll at
                        md+, like PlanNodeDetailPanel); the other tabs keep the
                        single padded scroll body. */}
                    {activeTab === 'overview' ? (
                        <div className="flex-1 min-h-0 overflow-hidden">
                            <OverviewPanel node={node} artifactPaths={artifactPaths} onRequirementClick={onRequirementClick} onSelectNode={onSelectNode} onOpenArtifact={onOpenArtifact} />
                        </div>
                    ) : (
                        <div className="flex-1 overflow-y-auto px-4 py-3">
                            {activeTab === 'data'       && isInputData && (
                                <DataUploadPanel
                                    projectId={projectId}
                                    modelId={modelId}
                                    node={node}
                                    authToken={authToken}
                                    userId={userId}
                                />
                            )}
                            {activeTab === 'validation' && <ValidationTabBody node={node} results={validationResults} />}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

/* ---------- Overview tab (plan-panel-style section grid) ---------- */

/** Short display form of a workspace id for requirement chips — the tail is
 *  the distinctive part (ids share the `ws_<project>` prefix). Full id stays
 *  in the tooltip. */
function shortWorkspaceId(id: string): string {
    const bare = id.replace(/^ws_/, '');
    return bare.length > 12 ? `…${bare.slice(-8)}` : bare;
}

function InputDataStatus({ node }: { node: NodeDefinition }) {
    const meta = (node.metadata || {}) as any;
    const activeId: string | null =
        typeof meta.active_upload_id === 'string' && meta.active_upload_id.trim()
            ? meta.active_upload_id
            : null;
    const uploads: any[] = Array.isArray(meta.uploads) ? meta.uploads : [];
    const active = uploads.find((u) => u && u.upload_id === activeId) || null;

    return (
        <section className="px-4 py-3">
            <SectionTitle>Data source</SectionTitle>
            {/* Readiness keys off active_upload_id presence — the SAME predicate
                the grid-card badge uses — so the two never disagree. The matched
                upload record (if present) just enriches the line. */}
            {activeId ? (
                <div className="flex items-start gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-success flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                    <div className="min-w-0">
                        <div className="text-[12px] font-semibold text-fg truncate">
                            {active?.filename || activeId}
                        </div>
                        {active ? (
                            <div className="text-[11px] text-fg-secondary">
                                {formatNumber(active.row_count ?? 0)} rows · {active.column_count ?? 0} cols
                                {uploads.length > 1 ? ` · ${uploads.length} versions` : ''}
                            </div>
                        ) : (
                            <div className="font-mono text-[10px] text-fg-tertiary">active: {activeId}</div>
                        )}
                        <div className="text-[10px] text-fg-tertiary mt-0.5">
                            Switch versions in the Upload data tab.
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 text-warning flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                    <div className="min-w-0">
                        <div className="text-[12px] font-semibold text-fg">Required — no file uploaded yet</div>
                        <div className="text-[10px] text-fg-tertiary mt-0.5">
                            Upload a CSV/XLSX in the Upload data tab.
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

function OverviewPanel({ node, artifactPaths, onRequirementClick, onSelectNode, onOpenArtifact }: {
    node: NodeDefinition;
    artifactPaths: Record<string, string>;
    onRequirementClick?: (requirementKey: string) => void;
    onSelectNode?: (nodeId: string) => void;
    onOpenArtifact?: (nodeId: string, artifactName: string) => void;
}) {
    // Resolve sibling node names/types for the Inputs rows (plan rows show
    // node titles, not raw ids). NodeInfoBar only mounts under ProcessViewer,
    // which owns this store.
    const model = useProcessViewerStore((s) => s.model);
    const nodeById = (id: string) => model?.nodes.find((n) => n.node_id === id);

    const description = node.logic_description || node.description;

    // Owner redesign: "What this node does" is the primary information and owns
    // most of the real estate; Requirements / Inputs / Output stack in a narrow
    // right rail. Referenced things are clickable — Input rows select the source
    // node; Output rows open the produced artifact.
    return (
        <div className="h-full grid grid-cols-1 md:grid-cols-[1.6fr,1fr] divide-y md:divide-y-0 md:divide-x divide-border-default overflow-y-auto md:overflow-hidden">
            {/* PRIMARY — What this node does (wide) */}
            <section className="md:min-h-0 flex flex-col px-4 py-3 md:overflow-hidden">
                <SectionTitle>What this node does</SectionTitle>
                <div className="md:flex-1 md:min-h-0 md:overflow-y-auto pr-1 space-y-3">
                    {node.node_type === 'validate' && node.validates_that && (
                        <div className="p-2.5 rounded-md bg-success-bg border-l-[3px] border-success">
                            <div className="font-body text-[10px] font-bold text-success uppercase tracking-[0.08em] mb-1">
                                Validates that
                            </div>
                            <p className="text-[13px] text-fg leading-relaxed">
                                {decodeEntities(node.validates_that.claim)}
                            </p>
                            {node.validates_that.citation && (
                                <div className="mt-1.5 font-mono text-[11px] text-fg-tertiary">
                                    <span className="text-fg-tertiary">cite: </span>
                                    <span className="text-fg-secondary">{decodeEntities(node.validates_that.citation)}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {description ? (
                        <p className="text-[13px] text-fg-secondary leading-relaxed whitespace-pre-wrap">
                            {decodeEntities(description)}
                        </p>
                    ) : !node.validates_that && node.logic_steps.length === 0 ? (
                        <p className="text-[12px] text-fg-tertiary italic">No description recorded yet.</p>
                    ) : null}

                    {node.logic_steps.length > 0 && (
                        <ol className="space-y-1.5">
                            {node.logic_steps.map((step, i) => (
                                <li key={i} className="flex gap-2 text-[12px] text-fg-secondary leading-relaxed">
                                    <span className="font-mono text-[10px] text-fg-tertiary mt-0.5 flex-shrink-0 w-4">
                                        {i + 1}.
                                    </span>
                                    <span>{decodeEntities(step)}</span>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            </section>

            {/* RIGHT RAIL — Requirements / Inputs / Output, stacked */}
            <div className="md:min-h-0 md:overflow-y-auto divide-y divide-border-default">
                {/* Requirements (typed requirement_refs; legacy fallback) */}
                <section className="px-4 py-3">
                    <SectionTitle>
                        Requirements{(node.requirement_refs?.length ?? 0) > 0 ? ` · ${node.requirement_refs!.length}` : ''}
                    </SectionTitle>
                    <RequirementsSection node={node} onRequirementClick={onRequirementClick} />
                </section>

                {/* Inputs (upstream dependencies) — click a row to select its source node */}
                <section className="px-4 py-3">
                    <SectionTitle>Inputs · {node.inputs.length}</SectionTitle>
                    {node.inputs.length === 0 ? (
                        <p className="text-[12px] text-fg-tertiary leading-snug">
                            No upstream inputs — this node starts from its own configuration.
                        </p>
                    ) : (
                        <ul className="divide-y divide-border-default">
                            {node.inputs.map((inp) => {
                                const src = nodeById(inp.source_node_id);
                                const canSelect = !!(src && onSelectNode);
                                const RowTag: any = canSelect ? 'button' : 'div';
                                return (
                                    <li key={`${inp.source_node_id}::${inp.artifact_name}`} className="first:pt-0">
                                        <RowTag
                                            {...(canSelect ? { onClick: () => onSelectNode!(inp.source_node_id), type: 'button' } : {})}
                                            className={`w-full text-left py-2 ${canSelect ? 'rounded hover:bg-bg-warm transition-colors cursor-pointer' : ''}`}
                                            title={canSelect ? 'Go to source node' : inp.source_node_id}
                                        >
                                            <div className="flex items-center gap-1.5 min-w-0">
                                                <span
                                                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                                    style={{ background: src ? getNodeTypeColor(src.node_type) : 'var(--fg-tertiary)' }}
                                                />
                                                <span className="font-mono text-[12px] font-semibold text-fg truncate">
                                                    {decodeEntities(inp.artifact_name)}
                                                </span>
                                                {inp.required && (
                                                    <span className="font-mono text-[9px] uppercase tracking-wider text-danger bg-danger-bg px-1 py-px rounded flex-shrink-0">
                                                        required
                                                    </span>
                                                )}
                                                {canSelect && <ArrowRight className="w-3 h-3 text-fg-tertiary flex-shrink-0 ml-auto" strokeWidth={1.5} />}
                                            </div>
                                            <p className="mt-0.5 pl-3 text-[11px] text-fg-secondary leading-snug truncate">
                                                {inp.expected_type} · from {src?.name ? decodeEntities(src.name) : inp.source_node_id}
                                            </p>
                                        </RowTag>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>

                {/* Data source (INPUT_DATA only) — answers "is data available?".
                    An input node has no upstream inputs but DOES have a data
                    requirement; surface the uploaded file (or the missing-data
                    warning) so the requirement's status is visible at a glance. */}
                {node.node_type === 'input_data' && (
                    <InputDataStatus node={node} />
                )}

                {/* Output (declared artifacts) — click a row to open the produced file */}
                <section className="px-4 py-3">
                    <SectionTitle>Output{node.outputs.length > 1 ? ` · ${node.outputs.length}` : ''}</SectionTitle>
                    {node.outputs.length === 0 ? (
                        <p className="text-[12px] text-fg-tertiary leading-snug">
                            No declared outputs.
                        </p>
                    ) : (
                        <ul className="divide-y divide-border-default">
                            {node.outputs.map((out) => {
                                const artifactPath = artifactPaths[out.artifact.artifact_name];
                                const canOpen = !!onOpenArtifact;
                                const RowTag: any = canOpen ? 'button' : 'div';
                                return (
                                    <li key={out.artifact.artifact_name} className="first:pt-0">
                                        <RowTag
                                            {...(canOpen ? { onClick: () => onOpenArtifact!(node.node_id, out.artifact.artifact_name), type: 'button' } : {})}
                                            className={`w-full text-left py-2 ${canOpen ? 'rounded hover:bg-bg-warm transition-colors cursor-pointer' : ''}`}
                                            title={canOpen ? (artifactPath ? `Open ${artifactPath}` : 'Open the output tool') : undefined}
                                        >
                                            <div className="flex items-center gap-1.5 min-w-0">
                                                <span className="font-mono text-[12px] font-semibold text-fg truncate">
                                                    {decodeEntities(out.artifact.artifact_name)}
                                                </span>
                                                <span className="font-mono text-[9px] uppercase tracking-wider text-accent-hover bg-accent-tint px-1 py-px rounded flex-shrink-0">
                                                    {out.artifact.artifact_type}
                                                </span>
                                                {canOpen && <ExternalLink className="w-3 h-3 text-fg-tertiary flex-shrink-0 ml-auto" strokeWidth={1.5} />}
                                            </div>
                                            {artifactPath && (
                                                <p className="mt-1 font-mono text-[10px] text-fg-tertiary leading-snug break-all">
                                                    {artifactPath}
                                                </p>
                                            )}
                                            {out.artifact.description && (
                                                <p className="mt-0.5 text-[11px] text-fg-secondary leading-snug">
                                                    {decodeEntities(out.artifact.description)}
                                                </p>
                                            )}
                                        </RowTag>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
}

/** REQUIREMENTS section body — typed requirement_refs are first-class;
 *  legacy handoff_item_ids render ONLY when no typed ref exists. With an
 *  onRequirementClick callback (threaded from ProcessViewer), typed chips
 *  click through to the Spec panel focused on that requirement; without it
 *  they stay inert spans. Legacy chips are never clickable — they carry no
 *  composite key the Spec panel could resolve. */
function RequirementsSection({ node, onRequirementClick }: {
    node: NodeDefinition;
    onRequirementClick?: (requirementKey: string) => void;
}) {
    const refs = node.requirement_refs ?? [];
    const legacy = node.handoff_item_ids ?? [];
    const proseMentionsReq = useMemo(() => {
        const text = [node.description, node.logic_description, ...node.logic_steps]
            .filter(Boolean)
            .join(' ');
        return /REQ-[0-9]/i.test(text);
    }, [node.description, node.logic_description, node.logic_steps]);

    if (refs.length > 0) {
        return (
            <ul className="space-y-1.5">
                {refs.map((ref) => {
                    const requirementKey = `${ref.workspace_id}::${ref.requirement_id}`;
                    const chipContent = (
                        <>
                            <Link2 className="w-3 h-3 text-fg-tertiary flex-shrink-0" strokeWidth={1.5} />
                            <span className="font-mono text-[11px] font-semibold text-fg flex-shrink-0">
                                {ref.requirement_id}
                            </span>
                            <span className="font-mono text-[10px] text-fg-tertiary truncate">
                                {shortWorkspaceId(ref.workspace_id)}
                            </span>
                        </>
                    );
                    return (
                        <li key={requirementKey}>
                            {onRequirementClick ? (
                                <button
                                    onClick={() => onRequirementClick(requirementKey)}
                                    className="inline-flex max-w-full items-center gap-1.5 px-2 py-1 rounded-md border border-border-default bg-bg-warm/50 hover:bg-bg-warm hover:border-border-strong transition-colors text-left"
                                    title={`${requirementKey}${ref.impl_note ? ` — ${ref.impl_note}` : ''}\nOpen in the spec panel`}
                                >
                                    {chipContent}
                                </button>
                            ) : (
                                <span
                                    className="inline-flex max-w-full items-center gap-1.5 px-2 py-1 rounded-md border border-border-default bg-bg-warm/50"
                                    title={`${requirementKey}${ref.impl_note ? ` — ${ref.impl_note}` : ''}`}
                                >
                                    {chipContent}
                                </span>
                            )}
                            {ref.impl_note && (
                                <p className="mt-0.5 pl-1 text-[11px] text-fg-secondary leading-snug line-clamp-2">
                                    {decodeEntities(ref.impl_note)}
                                </p>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    }

    return (
        <div className="space-y-2">
            {legacy.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {legacy.map((id) => (
                        <span
                            key={id}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-border-default bg-bg-warm/50 font-mono text-[10px] text-fg-secondary"
                            title="Legacy free-text handoff item — not a typed requirement link"
                        >
                            {id}
                            <span className="text-[9px] uppercase tracking-wider text-fg-tertiary bg-bg-warm px-1 rounded">
                                legacy
                            </span>
                        </span>
                    ))}
                </div>
            )}
            {proseMentionsReq ? (
                <p className="text-[11px] text-fg-tertiary leading-snug italic">
                    Requirements mentioned in the description but not linked — link
                    them with <span className="font-mono not-italic">link_requirement</span> so
                    the dependency is tracked.
                </p>
            ) : legacy.length === 0 ? (
                <p className="text-[12px] text-fg-tertiary leading-snug">
                    No requirements linked to this node.
                </p>
            ) : null}
        </div>
    );
}

/* ---------- Panel components ---------- */

/** Friendly axis label for a review channel — maps the shared ReviewChannel
 *  vocabulary onto the owner's validation axes (requirement / logic / docs). */
const REVIEW_CHANNEL_LABEL: Record<string, string> = {
    grounding: 'Requirement representation',
    coherence: 'Logic correctness',
    completeness: 'Completeness',
    documentation: 'Documentation',
    general: 'Review',
};

function ReviewVerdictPill({ verdict }: { verdict?: string }) {
    const v = verdict || 'unknown';
    const style = v === 'approved' ? 'bg-success-bg text-success'
        : v === 'rejected' ? 'bg-danger-bg text-danger'
        : v === 'flagged' || v === 'review_needed' ? 'bg-warning-bg text-warning'
        : 'bg-bg-warm text-fg-tertiary';
    return (
        <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-px rounded ${style}`}>
            {v.replace('_', ' ')}
        </span>
    );
}

/** Validation tab = the documented REVIEW (validate_work / validate_model_work
 *  NodeReviews — claim + verdict + rationale + evidence, like research) PLUS the
 *  RUNTIME CHECKS from the last execution. Mirrors the research-workspace
 *  validation surface so model nodes "document the validation" the same way. */
function ValidationTabBody({ node, results }: {
    node: NodeDefinition;
    results: import('../../types/modeling').ValidationResult[];
}) {
    const reviews = node.reviews ?? [];
    const status = node.validation_status;

    if (reviews.length === 0 && !status && results.length === 0) {
        return (
            <EmptyHint>
                Validation hasn't run for this node yet — it runs the same way as other workspaces,
                when the deliverable is validated.
            </EmptyHint>
        );
    }

    return (
        <div className="space-y-4">
            {/* REVIEW — does this node do what it should? */}
            <div>
                <div className="flex items-center gap-2 mb-1.5">
                    <SectionTitle>Review</SectionTitle>
                    {status && <ReviewVerdictPill verdict={status} />}
                </div>
                {reviews.length === 0 ? (
                    <p className="text-[12px] text-fg-tertiary leading-snug">
                        No documented review for this node yet.
                    </p>
                ) : (
                    <ul className="space-y-2">
                        {reviews.map((r, i) => {
                            const channel = String((r as Record<string, unknown>).channel || 'general');
                            const verdict = r.verdict;
                            const evidence = (((r as Record<string, unknown>).evidence_refs as string[] | undefined) || []);
                            const reviewerId = (r as Record<string, unknown>).reviewer_id;
                            const passed = verdict === 'approved';
                            const isRej = verdict === 'rejected';
                            const Icon = passed ? CheckCircle2 : isRej ? XCircle : AlertCircle;
                            const iconC = passed ? 'text-success' : isRej ? 'text-danger' : 'text-warning';
                            const bg = passed ? 'bg-success-bg' : isRej ? 'bg-danger-bg' : 'bg-warning-bg';
                            const border = passed ? 'border-success' : isRej ? 'border-danger' : 'border-warning';
                            return (
                                <li key={r.review_id || `${channel}-${i}`} className={`flex items-start gap-2.5 p-2.5 rounded-md border-l-[3px] ${bg} ${border}`}>
                                    <Icon className={`w-4 h-4 ${iconC} flex-shrink-0 mt-0.5`} strokeWidth={1.5} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[13px] font-semibold text-fg">
                                                {REVIEW_CHANNEL_LABEL[channel] || channel}
                                            </span>
                                            <ReviewVerdictPill verdict={verdict} />
                                            {reviewerId ? (
                                                <span className="font-mono text-[10px] text-fg-tertiary truncate">{String(reviewerId)}</span>
                                            ) : null}
                                        </div>
                                        {r.rationale ? (
                                            <p className="mt-1 text-[12px] text-fg-secondary leading-relaxed whitespace-pre-wrap">
                                                {decodeEntities(String(r.rationale))}
                                            </p>
                                        ) : null}
                                        {evidence.length > 0 && (
                                            <div className="mt-1 flex flex-wrap gap-1">
                                                {evidence.map((e) => (
                                                    <span key={e} className="font-mono text-[10px] text-fg-tertiary bg-bg-warm px-1.5 py-px rounded">
                                                        {e}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* RUNTIME CHECKS — did the data pass on the last run? */}
            {results.length > 0 && (
                <div>
                    <SectionTitle>Runtime checks · last run</SectionTitle>
                    <div className="mt-1.5">
                        <ValidationPanel results={results} />
                    </div>
                </div>
            )}

            <p className="text-[11px] text-fg-tertiary leading-snug">
                Code inspection is full for Jupyter nodes; Python / Sheets nodes validate declared
                logic + documentation (their code lives outside BARK).
            </p>
        </div>
    );
}

function ValidationPanel({ results }: { results: import('../../types/modeling').ValidationResult[] }) {
    if (results.length === 0) {
        return <EmptyHint>No validation results for this node.</EmptyHint>;
    }
    return (
        <ul className="space-y-2">
            {results.map((vr) => {
                const isError = !vr.passed && vr.severity === 'error';
                const isWarn  = !vr.passed && vr.severity === 'warning';
                const Icon   = vr.passed ? CheckCircle2 : isError ? XCircle : AlertCircle;
                const iconC  = vr.passed ? 'text-success' : isError ? 'text-danger' : 'text-warning';
                const bg     = vr.passed ? 'bg-success-bg' : isError ? 'bg-danger-bg' : 'bg-warning-bg';
                const border = vr.passed ? 'border-success' : isError ? 'border-danger' : 'border-warning';
                return (
                    <li
                        key={vr.check_name}
                        className={`flex items-start gap-2.5 p-2.5 rounded-md border-l-[3px] ${bg} ${border}`}
                    >
                        <Icon className={`w-4 h-4 ${iconC} flex-shrink-0 mt-0.5`} strokeWidth={1.5} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="text-[13px] font-semibold text-fg">{decodeEntities(vr.check_name)}</span>
                            </div>
                            {(vr.expected_value || vr.actual_value) && (
                                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[11px]">
                                    {vr.expected_value && (
                                        <span className="text-fg-tertiary">
                                            expected: <span className="text-fg-secondary">{vr.expected_value}</span>
                                        </span>
                                    )}
                                    {vr.actual_value && (
                                        <span className="text-fg-tertiary">
                                            actual: <span className={vr.passed ? 'text-success' : 'text-danger'}>{vr.actual_value}</span>
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

/* ---------- Small shared UI ---------- */

function SectionTitle({ children }: { children: React.ReactNode }) {
    return (
        <div className="font-body text-[10px] font-bold uppercase tracking-[0.08em] text-fg-tertiary mb-1.5 flex-shrink-0">
            {children}
        </div>
    );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex items-center gap-2 text-sm text-fg-tertiary italic">
            <FileCog className="w-4 h-4" strokeWidth={1.5} />
            <span>{children}</span>
        </div>
    );
}

function StatusPill({ status, timeMs }: { status: RunStatus; timeMs?: number }) {
    const style: Record<RunStatus, { bg: string; text: string; dot: string; icon?: React.ReactNode }> = {
        pending:   { bg: 'bg-bg-warm',    text: 'text-fg-tertiary', dot: 'bg-fg-tertiary' },
        running:   { bg: 'bg-warning-bg', text: 'text-warning',     dot: 'bg-warning',
                     icon: <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} /> },
        completed: { bg: 'bg-success-bg', text: 'text-success',     dot: 'bg-success' },
        failed:    { bg: 'bg-danger-bg',  text: 'text-danger',      dot: 'bg-danger' },
    };
    const s = style[status];
    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-mono text-[10px] font-semibold uppercase tracking-wider ${s.bg} ${s.text}`}>
            {s.icon ?? <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />}
            <span>{status}</span>
            {timeMs !== undefined && (
                <span className="text-fg-tertiary ml-1">
                    {timeMs < 1000 ? `${timeMs}ms` : `${(timeMs / 1000).toFixed(1)}s`}
                </span>
            )}
        </span>
    );
}
